import { Box, Stack, Typography } from '@mui/material';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AICheckButton } from 'components/AI/AIButton/AICheckButton';
import { AIFileNameSuggestions } from 'components/AI/AIFileNameManagement/components/AIFileNameSuggestions/AIFileNameSuggestions';
import { AutoResizeTextarea } from 'components/AutoResizeTextarea';
import { removeFileExtension } from 'components/AI/AIFileNameManagement/utils/removeFileExtension';

interface FormValues {
  newFileName: string;
}

export interface AIFileNameFormProps {
  fileUuid: string;
  onSubmit?: (data: FormValues) => void;
  isLoading?: boolean;
  fileName?: string;
}

const schema = yup.object().shape({
  newFileName: yup
    .string()
    .required('Filename is required')
    .min(3, 'Filename must be at least 3 characters'),
});

export const AIFileNameForm = ({
  fileUuid,
  onSubmit = () => {},
  isLoading = false,
  fileName,
}: AIFileNameFormProps) => {
  const formContextValue = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      newFileName: removeFileExtension(fileName),
    },
  });

  const onFormSubmit = (data: FormValues) => {
    onSubmit(data);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = formContextValue;

  // Watch for changes to newFileName field (needed for external changes from suggestions)
  const newFileNameValue = watch('newFileName');

  return (
    <FormProvider {...formContextValue}>
      <Stack gap={2} mt={2}>
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={600}>
            New File Name
          </Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            <div
              className={
                errors.newFileName
                  ? 'd-block form-group form-error mb-0'
                  : 'd-block form-group mb-0'
              }
              style={{ flex: 1 }}
            >
              <AutoResizeTextarea
                {...register('newFileName')}
                className="form-control"
                autoComplete="off"
                placeholder="Enter custom name"
                disabled={isLoading}
                // Pass the watched value to ensure the textarea resizes when changed externally
                value={newFileNameValue}
                onKeyDown={(e) => {
                  // We can't use a normal form element here because this component is rendered inside another form
                  // Instead, we manually trigger the submit handler when Enter is pressed
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(onFormSubmit)(e);
                  }
                }}
              />
            </div>
            <Box>
              <AICheckButton
                onClick={handleSubmit(onFormSubmit)}
                disabled={isLoading}
              />
            </Box>
          </Stack>
          <span className={errors.newFileName ? 'error_label' : 'd-none'}>
            {errors.newFileName?.message}
          </span>
        </Stack>
        <AIFileNameSuggestions fileUuid={fileUuid} isFormLoading={isLoading} />
      </Stack>
    </FormProvider>
  );
};
